import { createFileRoute, Outlet } from '@tanstack/react-router';
import { commonSearchSchema } from '@/routes/-shared/commonSearchSchema';
import { RoleGuard } from '@/components/molecules/role-guard';
import { usePermissions } from '@/hooks/use-permissions';
import { useTenantFeatureFlags } from '@/lib/hooks/use-tenant-feature-flags';
import { useCommercialModules } from '@/lib/hooks/use-commercial-modules';
import z from '@/lib/zod';

/**
 * Search schema della pagina inventario.
 *
 * Estende `commonSearchSchema` (search, skip, take, sort, show, tab) con i
 * filtri specifici: `warehouseId` e `status`. Il flag access gate è
 * `canAccessRegistry` (storicamente raggruppato con anagrafica/magazzino —
 * stesso pattern di `pages/products` e `pages/warehouse`).
 */
export const inventorySearchSchema = commonSearchSchema.extend({
  warehouseId: z.string().nullish(),
  status: z.string().nullish(),
});

export type InventorySearchSchema = z.infer<typeof inventorySearchSchema>;

export const Route = createFileRoute('/protected/_layout/inventory/_layout')({
  validateSearch: inventorySearchSchema,
  component: InventoryLayout,
});

function InventoryLayout() {
  // Listing consolidato sotto `/warehouse?subTab=inventory`: solo le
  // route detail (`/inventory/$id`, `/inventory/new`) restano qui per la
  // SheetView del form sessione. La pagina nuda redireziona via
  // `_layout.index.tsx`.
  const { canAccessWarehouse, isLoading } = usePermissions();
  const { isWarehouseEnabled, isLoading: isFeatureFlagsLoading } =
    useTenantFeatureFlags();
  // Inventario fisico: modulo Magazzino del contratto (listino 2026-10).
  const { isWarehouseIncluded } = useCommercialModules();

  return (
    <RoleGuard
      hasAccess={
        canAccessWarehouse && isWarehouseEnabled && isWarehouseIncluded
      }
      isLoading={isLoading || isFeatureFlagsLoading}
    >
      <Outlet />
    </RoleGuard>
  );
}
